<script lang="ts">
  import type { DateSelection, FormatType } from "../../models/when-search.interface";
  import { SearchState } from "../../services/search-service";
  import { deleteWhenSearchQueryStringParameter, setQueryStringParameter } from "../../url";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";

  export let format: FormatType = ""; // Can be Date, DateTime, Month, Month/Day, Month/Day/Hour
  export let minDate: string = "1842-10-25";
  export let maxDate: string = "2024-09-09";
  export let enabled: boolean = true;

  let checked: boolean = false;
  let startDate: string;
  let endDate: string;
  let startDateSelection: DateSelection = { month: "MM", day: "DD", hour: "HH" };
  let endDateSelection: DateSelection = { month: "MM", day: "DD", hour: "HH" };

  $: {
    startDate = $SearchState.params.get("startDate") ?? "";
    endDate = $SearchState.params.get("endDate") ?? "";

    if (startDate && !startDateSelection.month) {
      const date = new Date(startDate);
      startDateSelection = {
        month: (date.getMonth() + 1).toString().padStart(2, "0"),
        day: date.getDate().toString().padStart(2, "0"),
        hour: date.getHours().toString().padStart(2, "0")
      };
    }

    if (endDate && !endDateSelection.month) {
      const date = new Date(endDate);
      endDateSelection = {
        month: (date.getMonth() + 1).toString().padStart(2, "0"),
        day: date.getDate().toString().padStart(2, "0"),
        hour: date.getHours().toString().padStart(2, "0")
      };
    }
  }

  const monthNames: string[] = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  let days: string[] = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
  const hours: string[] = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

  // Update the days dropdown based on the selected month
  function updateDaysDropdown(month: string) {
    if (month !== "MM") {
      const daysInMonth = getDaysInMonth(parseInt(month, 10));
      days = Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, "0"));
    }
  }

  // Get the number of days in a given month and year
  function getDaysInMonth(month: number, year: number = new Date().getFullYear()): number {
    return new Date(year, month, 0).getDate();
  }

  function setStartDate() {
    if (!isValidSelection(startDateSelection, format)) {
      return;
    }

    const year = getYearForMonth(startDateSelection.month);
    const formattedStartDate = assembleFormattedDate(startDateSelection, year, format, false);
    const formattedEndDate = checked
      ? endDate
      : assembleFormattedDate(startDateSelection, year, format, true);

    if (!isDateRangeValid(formattedStartDate, formattedEndDate)) {
      console.error("Date range is invalid");
      return;
    }

    setQueryStringParameter("startDate", formattedStartDate);
    if (!checked) {
      setQueryStringParameter("endDate", formattedEndDate);
    }
  }

  function setEndDate() {
    if (!isValidSelection(endDateSelection, format)) {
      return;
    }

    const year = getYearForMonth(endDateSelection.month);
    const formattedEndDate = assembleFormattedDate(endDateSelection, year, format, true);

    if (!isDateRangeValid(startDate, formattedEndDate)) {
      console.error("Date range is invalid");
      return;
    }
    setQueryStringParameter("endDate", formattedEndDate);
  }

  // Determine the correct year based on the selected month
  function getYearForMonth(selectedMonth: string): number {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    return parseInt(selectedMonth, 10) <= currentMonth ? currentYear : currentYear - 1;
  }

  // Validate that the date components are selected
  function isValidSelection(dateObj: DateSelection, format: FormatType): boolean {
    const { month, day, hour } = dateObj;
    if (month === "MM") return false;
    if (format.includes("Day") && day === "DD") return false;
    if (format.includes("Hour") && hour === "HH") return false;
    return true;
  }

  // Assemble a formatted date string from date components
  function assembleFormattedDate(
    dateObj: DateSelection,
    year: number,
    format: FormatType,
    isEnd: boolean
  ): string {
    const { month, day, hour } = dateObj;
    let dateStr = `${year}-${month}`;

    if (format.includes("Day")) {
      dateStr += `-${day}`;
    } else {
      dateStr += `-01`;
    }

    if (format.includes("Hour")) {
      dateStr += `T${hour}:${isEnd ? "59" : "00"}:00`;
    } else {
      dateStr += `T${isEnd ? "23:59:59" : "00:00:00"}`;
    }

    return dateStr;
  }

  function isDateRangeValid(start: string, end: string): boolean {
    if (!start || !end) return true;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return startDate <= endDate;
  }

  // Clear all date selections and query parameters
  function clearDates() {
    deleteWhenSearchQueryStringParameter("startDate", "endDate");
    startDateSelection = { month: "MM", day: "DD", hour: "HH" };
    endDateSelection = { month: "MM", day: "DD", hour: "HH" };
    startDate = "";
    endDate = "";
  }
</script>

<li class="mb-1">
  <div class="pb-2">
    When
    <TooltipHybridButton
      searchUrlQueryParam="month"
      tooltip="
      <p>
        Enter a single date or click the Select Date Range option to enter both a start and end
        date.
      </p>
      <p>
        Date Format: <b> {format} </b>
      </p>"
    />
    <!--    <i-->
    <!--      id="monthToolTip"-->
    <!--      class="bi bi-question-circle-fill"-->
    <!--      aria-label="Month Search Tool Tip"-->
    <!--      aria-describedby="monthSearchToolTipContent"-->
    <!--    ></i>-->
    <!--    <Tooltip id="monthSearchToolTipContent" target="monthToolTip" placement="right" role="tooltip">-->
    <!--      <p>-->
    <!--        Enter a single date or click the Select Date Range option to enter both a start and end-->
    <!--        date.-->
    <!--      </p>-->
    <!--      <p>-->
    <!--        Date Format: <b> {format} </b>-->
    <!--      </p>-->
    <!--    </Tooltip>-->
  </div>

  <!-- Start Date Inputs -->
  <form>
    <div class="row g-2 mt-1">
      <div class={format.includes("Day") ? "col-6 col-sm-4" : "col-12"}>
        <select
          class="form-select form-select-compact"
          bind:value={startDateSelection.month}
          on:change={() => {
            if (format.includes("Day")) updateDaysDropdown(startDateSelection.month);
            setStartDate();
          }}
        >
          <option value="MM" disabled selected>Month</option>
          {#each monthNames as name, i}
            <option value={(i + 1).toString().padStart(2, "0")}>{name}</option>
          {/each}
        </select>
      </div>

      {#if format.includes("Day")}
        <div class="col-6 col-sm-4">
          <select
            class="form-select form-select-compact"
            bind:value={startDateSelection.day}
            on:change={setStartDate}
          >
            <option value="DD" disabled selected>Day</option>
            {#each days as day}
              <option value={day}>{day}</option>
            {/each}
          </select>
        </div>
      {/if}

      {#if format.includes("Hour")}
        <div class="col-12 col-sm-4">
          <select
            class="form-select form-select-compact"
            bind:value={startDateSelection.hour}
            on:change={setStartDate}
          >
            <option value="HH" disabled selected>Hour</option>
            {#each hours as hour}
              <option value={hour}>{hour}</option>
            {/each}
          </select>
        </div>
      {/if}
    </div>

    <!-- End Date Inputs -->
    {#if checked}
      <div class="row g-2 mt-2">
        <div class={format.includes("Day") ? "col-6 col-sm-4" : "col-12"}>
          <select
            class="form-select form-select-compact"
            bind:value={endDateSelection.month}
            on:change={() => {
              if (format.includes("Day")) updateDaysDropdown(endDateSelection.month);
              setEndDate();
            }}
          >
            <option value="MM" disabled selected>Month</option>
            {#each monthNames as name, i}
              <option value={(i + 1).toString().padStart(2, "0")}>{name}</option>
            {/each}
          </select>
        </div>

        {#if format.includes("Day")}
          <div class="col-6 col-sm-4">
            <select
              class="form-select form-select-compact"
              bind:value={endDateSelection.day}
              on:change={setEndDate}
            >
              <option value="DD" disabled selected>Day</option>
              {#each days as day}
                <option value={day}>{day}</option>
              {/each}
            </select>
          </div>
        {/if}

        {#if format.includes("Hour")}
          <div class="col-12 col-sm-4">
            <select
              class="form-select form-select-compact"
              bind:value={endDateSelection.hour}
              on:change={setEndDate}
            >
              <option value="HH" disabled selected>Hour</option>
              {#each hours as hour}
                <option value={hour}>{hour}</option>
              {/each}
            </select>
          </div>
        {/if}
      </div>
    {/if}
  </form>

  <!-- Toggle Date Range Selection -->
  <div class="form-check mt-2">
    <input class="form-check-input" type="checkbox" bind:checked id="date-range-check" />
    <label class="form-check-label" for="date-range-check"> Select Date Range </label>
  </div>

  <!-- Display Start and End Dates with Time -->
  {#if startDate && endDate}
    <div class="d-flex flex-column gap-2 py-1">
      <span
        class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1 text-truncate"
        role="status"
        aria-label="when search start date search pill: {startDate}"
      >
        <button type="button" class="btn p-0 m-0" on:click={clearDates}>
          <i class="bi bi-x text-bg-dark" aria-label="Delete {startDate}"></i>
        </button>
        Start date: {startDate}
      </span>
      <span
        class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1 text-truncate"
        role="status"
        aria-label="when search end date search pill: {endDate}"
      >
        <button type="button" class="btn p-0 m-0" on:click={clearDates}>
          <i class="bi bi-x text-bg-dark" aria-label="Delete {endDate}"></i>
        </button>
        End date: {endDate}
      </span>
    </div>
  {/if}
</li>

<style>
    .form-select-compact {
        padding-right: 2rem;
        font-size: 0.9rem;
    }
</style>
